export interface DurableQueueSnapshotItem {
  id: string;
  message: string;
  mode: "queued" | "steering";
  createdAt: string;
  context?: {
    referenceContext?: Record<string, unknown>;
    riskFlowContext?: string;
  };
  attachmentCount: number;
}

export function readDurableQueueSnapshot(
  projection: Record<string, unknown>,
): DurableQueueSnapshotItem[] {
  const tasks = Array.isArray(projection.tasks) ? projection.tasks : [];
  const activeTask = isRecord(projection.activeTask)
    ? [projection.activeTask]
    : [];
  const seen = new Set<string>();
  const items: DurableQueueSnapshotItem[] = [];

  for (const task of [...activeTask, ...tasks]) {
    if (!isRecord(task) || !isRecord(task.metadata)) continue;
    const metadata = task.metadata;
    const queues = [metadata.pendingQueue, metadata.queueDispatchBatch];
    for (const queue of queues) {
      if (!Array.isArray(queue)) continue;
      for (const value of queue) {
        const item = readQueueItem(value);
        if (!item || seen.has(item.id)) continue;
        seen.add(item.id);
        items.push(item);
      }
    }
  }

  return items.sort((left, right) => {
    if (left.mode !== right.mode) return left.mode === "steering" ? -1 : 1;
    return Date.parse(left.createdAt) - Date.parse(right.createdAt);
  });
}

function readQueueItem(value: unknown): DurableQueueSnapshotItem | null {
  if (!isRecord(value)) return null;
  if (typeof value.id !== "string" || typeof value.message !== "string")
    return null;
  if (value.mode !== "queued" && value.mode !== "steering") return null;
  if (typeof value.createdAt !== "string") return null;
  const context = isRecord(value.context)
    ? {
        ...(isRecord(value.context.referenceContext)
          ? { referenceContext: value.context.referenceContext }
          : {}),
        ...(typeof value.context.riskFlowContext === "string"
          ? { riskFlowContext: value.context.riskFlowContext }
          : {}),
      }
    : undefined;
  return {
    id: value.id,
    message: value.message,
    mode: value.mode,
    createdAt: value.createdAt,
    ...(context ? { context } : {}),
    attachmentCount: Array.isArray(value.attachments)
      ? value.attachments.length
      : 0,
  };
}

function isRecord(value: unknown): value is Record<string, any> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
